import * as React from "react"
import type { MeterStatsType, PaginationType, StatsValue } from "@/types"
import { useQueries, useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import type { DateRange } from "react-day-picker"

import {
  meterDailyAnalyticsCountType,
  // MeterInfoAnalyticsType,
  MeterInfoType,
  MeterManagementAnalyticsType,
} from "@/types/meter"
import { meterChartConfig } from "@/config/charts"
import { canViewThisPage, validateMeterInfoPermission } from "@/lib/api/auth"
import {
  getMeterDailyAnalyticsCount,
  getMeterInfo,
  // getMeterInfoAnalytics,
  getMeterManagementAnalytics,
  getMeterStatistics,
} from "@/lib/api/meter"
import { getStatusStyle } from "@/lib/statusStyles"
import { buttonVariants } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartConfig } from "@/components/ui/chart"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { BarChartStacked } from "@/components/charts/barchart-stacked"
import { PieChartComponent } from "@/components/charts/piechart"
import { DataTable } from "@/components/data-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import SelectTimeRange, {
  timeRangeOptions,
} from "@/components/select-time-range"
import { meterInfoColumns } from "@/components/table/columns/meter-info-columns"
import { ExportOperationModal } from "@/components/table/operations/export-operations"

export const Route = createFileRoute("/_home/meter-info/")({
  beforeLoad: async () => {
    validateMeterInfoPermission()
  },
  component: MeterInfoPage,
})

function MeterInfoPage() {
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const [timeRange, setTimeRange] = React.useState(timeRangeOptions()[0])
  const [meterAnalyticsDate, setMeterAnalyticsDate] = React.useState<DateRange>(
    {
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    },
  )

  const [meterStaticticsDate, setMeterStaticticsDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      to: new Date(),
    })

  const [defaultPage, setDefaultPage] = React.useState<number>(10)
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState<string>("")

  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)

  const { data, isError, isLoading } = useQuery<PaginationType<MeterInfoType>>({
    queryKey: ["meterInfo", defaultPage, page, search],
    queryFn: () => getMeterInfo(defaultPage, page, search),
  })
  console.log(data)

  const {
    data: meterDailyAnalyticsCount,
    isLoading: meterDailyAnalyticsCountLoading,
  } = useQuery<meterDailyAnalyticsCountType>({
    queryKey: [
      "meterDailyAnalyticsCount",
      meterAnalyticsDate.from?.toISOString(),
      meterAnalyticsDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = meterAnalyticsDate.from ?? new Date()
      const to = meterAnalyticsDate.to ?? new Date()
      return await getMeterDailyAnalyticsCount(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  console.log(meterDailyAnalyticsCount)

  React.useEffect(() => {
    if (data) {
      setNextPageUrl(data.next)
      setPrevPageUrl(data.previous)
    }
  }, [data])

  // const { data: meterInfoAnalytics } = useQuery<MeterInfoAnalyticsType[]>({
  //   queryKey: ["meterInfoAnalytics", meterAnalyticsDate],
  //   queryFn: async () => {
  //     const from = meterAnalyticsDate.from ?? new Date()
  //     const to = meterAnalyticsDate.to ?? new Date()
  //     return await getMeterInfoAnalytics(from.toISOString(), to.toISOString())
  //   },
  // })

  const {
    data: meterManagementAnalytics,
    isLoading: isMeterManageAnalyticsLoading,
  } = useQuery<MeterManagementAnalyticsType>({
    queryKey: ["meterManagementAnalysis", meterStaticticsDate],
    queryFn: async () => {
      const from = meterStaticticsDate.from ?? new Date()
      const to = meterStaticticsDate.to ?? new Date()
      return await getMeterManagementAnalytics(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const { data: meterInfOverview, isLoading: isMeterInfoOverviewLoading } =
    useQuery<MeterStatsType>({
      queryKey: ["meterStats", timeRange.value],
      queryFn: () => getMeterStatistics(Number(timeRange.value)),
    })

  console.log(meterInfOverview?.data?.active_meters)

  const renderOverviewCard = (
    title: string,
    icon: keyof typeof Icons,
    data?: {
      total: number | string
      percentage: number | null
      status: string
    },
  ) => {
    const IconComponent = Icons[icon]
    const {
      color,
      bgColor,
      icon: StatusIcon,
    } = getStatusStyle(data?.status ?? "", data?.percentage ?? null)

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex gap-2 text-[13px] font-medium text-muted-foreground">
            {["Total Consumers", "Active Consumers"].includes(title) && (
              <Icons.person className="h-[18px] w-[18px]" />
            )}
            {[
              "Total Payments",
              "Total Billing",
              "Total Bill Adjustment",
            ].includes(title) && (
              <img
                src="peso-sign.png"
                alt="icon"
                className="h-[18px] w-[18px]"
              />
            )}
            {["Total Meters"].includes(title) && (
              <Icons.gauge className="h-[20px] w-[20px] text-[#00B6AE]" />
            )}
            {["Total Active"].includes(title) && (
              <Icons.check className="h-[20px] w-[20px] text-[#00B6AE]" />
            )}
            {["Total Inactive"].includes(title) && (
              <Icons.x className="w-[20px]text-red-500 h-[20px]" />
            )}
            {["Total Decommissioned"].includes(title) && (
              <Icons.archive className="h-[20px] w-[20px] text-red-500" />
            )}
            {["Total Unassigned"].includes(title) && (
              <Icons.power className="h-[20px] w-[20px] text-gray-500" />
            )}
            {["Before Due", "Pending Bill Adjustment"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-yellow-500" />
            )}
            {["Due Date", "Approved Bill Adjustment"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-primary" />
            )}
            {["Over Due"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-orange-500" />
            )}
            {["Disconnection", "Denied Bill Adjustment"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-red-600" />
            )}
            <p className="line-height-[14px] font-worksans font-bold tracking-wider">
              {title}
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-2xl font-bold">
          {isMeterInfoOverviewLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : data?.total !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <h1 className="font-semibold">
                {title === "Total Payments" ? `PHP ${data.total}` : data.total}
              </h1>
              {data.status !== "New" && (
                <span
                  className={`rounded-sm px-1 text-[13px] font-normal ${color} ${bgColor}`}
                >
                  {data.status !== "Flat" && data.percentage !== null
                    ? `${data.percentage.toFixed(2)}%`
                    : ""}

                  {StatusIcon && (
                    <span
                      className={
                        data.status === "Flat"
                          ? "flex items-center"
                          : "inline-block px-1"
                      }
                    >
                      {StatusIcon}
                    </span>
                  )}
                </span>
              )}
            </div>
          ) : (
            "N/A"
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <main className="space-y-4 p-4">
      <div className="flex w-full justify-between pt-4">
        <div>
          <Header>Meter Information</Header>
        </div>
        <div className="flex items-center gap-3">
          <Label>Set view</Label>
          <SelectTimeRange range={timeRange} setRange={setTimeRange} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        {renderOverviewCard(
          "Total Meters",
          "gauge",
          meterInfOverview?.data?.total_meters,
        )}
        {renderOverviewCard(
          "Total Active",
          "check",
          meterInfOverview?.data?.active_meters,
        )}
        {renderOverviewCard(
          "Total Inactive",
          "peso",
          meterInfOverview?.data?.inactive_meters,
        )}
        {renderOverviewCard(
          "Total Decommissioned",
          "peso",
          meterInfOverview?.data?.decommissioned_meters,
        )}
        {renderOverviewCard(
          "Total Unassigned",
          "peso",
          meterInfOverview?.data?.unassigned_meters,
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3">
        <Card className="col-span-2">
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Meter Analytics</CardTitle>
            <div>
              <DateRangePicker
                date={meterAnalyticsDate}
                setDate={setMeterAnalyticsDate}
              />
            </div>
          </CardHeader>
          <CardContent>
            {/* Show loading when refetching */}
            {meterDailyAnalyticsCountLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : meterDailyAnalyticsCount ? (
              <BarChartStacked
                data={meterDailyAnalyticsCount}
                config={meterChartConfig}
              />
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Meter Statistics</CardTitle>
            <DateRangePicker
              date={meterStaticticsDate}
              setDate={setMeterStaticticsDate}
            />
          </CardHeader>
          <CardContent>
            <div className="flex h-full items-center justify-center">
              {isMeterManageAnalyticsLoading ? (
                <div className="flex items-center justify-center">
                  <Skeleton className="h-60 w-60 rounded-full" />{" "}
                </div>
              ) : meterManagementAnalytics &&
                meterManagementAnalytics?.meter_status_distribution ? (
                <PieChartComponent
                  data={meterManagementAnalytics?.meter_status_distribution}
                  dataKey="count"
                  nameKey="meter_status"
                  className="height:1000px"
                />
              ) : (
                <div className="flex h-[240px] items-center justify-center text-gray-500">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <DataTable
        columns={meterInfoColumns}
        data={data?.results ?? []}
        defaultPage={defaultPage}
        search={{
          // field: "serial_number",
          label: "serial number",
          value: search,
          onChange: setSearch,
        }}
        isLoading={isLoading}
        isError={isError}
        setDefaultPage={setDefaultPage}
        nextPageUrl={nextPageUrl}
        prevPageUrl={prevPageUrl}
        totalCount={data?.count}
        page={page}
        setPage={setPage}
      >
        <div className="flex w-full justify-end">
          <Link
            to="/meter-info/add"
            className={buttonVariants({ variant: "default" })}
          >
            <Icons.circleGauge />
            Add Meter
          </Link>
        </div>
        <ExportOperationModal
          tableData={data?.results ?? [{}]}
          fileName="Meter Information"
          showExportModal={showExportModal}
          setShowExportModal={setShowExportModal}
        />
      </DataTable>
    </main>
  )
}
